import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Divider,
} from '@mui/material'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'

function formatOptional(value, suffix = '') {
  if (value == null || value === '') return '—'
  return `${value}${suffix}`
}

function formatList(list) {
  if (!Array.isArray(list) || list.length === 0) return '—'
  return list.join(', ')
}

/**
 * Read-only summary of risk-map calculation settings (from API metadata.settings).
 */
export default function RoadRiskMapSettingsDialog({ open, onClose, settings }) {
  const { _ } = useLingui()
  const s = settings || {}

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Trans>Road risk map settings</Trans>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{_(msg`Severity weights`)}</Typography>
          <Typography variant="body2">
            <Trans>Fatal</Trans>: {formatOptional(s.weightFatal)}
            {' · '}
            <Trans>Serious</Trans>: {formatOptional(s.weightSerious)}
            {' · '}
            <Trans>Slight</Trans>: {formatOptional(s.weightSlight)}
          </Typography>
          <Typography variant="body2">
            <Trans>No injury</Trans>: {formatOptional(s.weightNoInjury)}
            {' · '}
            <Trans>Unknown</Trans>: {formatOptional(s.weightUnknown)}
          </Typography>

          <Divider />

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{_(msg`Assignment & score`)}</Typography>
          <Typography variant="body2">
            <Trans>Snap threshold</Trans>: {formatOptional(s.snapThresholdMeters, ' m')}
          </Typography>
          <Typography variant="body2">
            <Trans>Min denominator length</Trans>: {formatOptional(s.minDenominatorLengthM, ' m')}
          </Typography>
          <Typography variant="body2">
            <Trans>Min crashes per segment</Trans>: {formatOptional(s.minCrashesPerSegment)}
          </Typography>
          <Typography variant="body2">
            <Trans>Top list size</Trans>: {formatOptional(s.topListSize)}
          </Typography>
          <Typography variant="body2">
            <Trans>Score decimals</Trans>: {formatOptional(s.scoreRoundingDecimals)}
          </Typography>
          <Typography variant="body2">
            <Trans>Unique segment assignment</Trans>: {s.assignUniqueSegment ? _(msg`Yes`) : _(msg`No`)}
          </Typography>

          <Divider />

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{_(msg`Road filters`)}</Typography>
          <Typography variant="body2">
            <Trans>Min segment length</Trans>: {formatOptional(s.minSegmentLengthM, ' m')}
          </Typography>
          <Typography variant="body2">
            <Trans>Max segment length</Trans>: {formatOptional(s.maxSegmentLengthM, ' m')}
          </Typography>
          <Typography variant="body2">
            <Trans>Included fclasses</Trans>: {formatList(s.includedFclasses)}
          </Typography>
          <Typography variant="body2">
            <Trans>Excluded fclasses</Trans>: {formatList(s.excludedFclasses)}
          </Typography>
          <Typography variant="body2">
            <Trans>Require named road</Trans>: {s.requireNamedRoad ? _(msg`Yes`) : _(msg`No`)}
          </Typography>
          {s.minWeightedSumPerSegment != null && (
            <Typography variant="body2">
              <Trans>Min weighted sum</Trans>: {s.minWeightedSumPerSegment}
            </Typography>
          )}
          {s.minRiskScore != null && (
            <Typography variant="body2">
              <Trans>Min risk score</Trans>: {s.minRiskScore}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <Trans>Close</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
